import TableContainer from "@material-ui/core/TableContainer";
import React, { useEffect, useReducer, useState } from "react";
import {
    Button,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import PageHelpGuide from "../../components/PageHelpGuide";
import { PROFESSIONALS_HELP_DATA } from "../../constants/pageHelp/professionalsHelp";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import ProfessionalModal from "../../components/ProfessionalModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32 !important" : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.type === "dark" ? "#1e2126" : "#f3f4f6",
    color: theme.palette.type === "dark" ? "#ffffff" : "#111827",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    zIndex: 1,
    position: "sticky",
    top: 0,
  },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_PROFESSIONALS") {
        const professionals = action.payload;
        const newProfessionals = [];

        professionals.forEach((p) => {
            const pIndex = state.findIndex((s) => s.id === p.id);
            if (pIndex !== -1) {
                state[pIndex] = p;
            } else {
                newProfessionals.push(p);
            }
        });

        return [...state, ...newProfessionals];
    }

    if (action.type === "UPDATE_PROFESSIONALS") {
        const professional = action.payload;
        const pIndex = state.findIndex((s) => s.id === professional.id);

        if (pIndex !== -1) {
            state[pIndex] = professional;
            return [...state];
        } else {
            return [professional, ...state];
        }
    }

    if (action.type === "DELETE_PROFESSIONAL") {
        const professionalId = action.payload;
        const pIndex = state.findIndex((s) => s.id === professionalId);
        if (pIndex !== -1) {
            state.splice(pIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const Professionals = () => {
    const classes = useStyles();

    const [professionals, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [professionalModalOpen, setProfessionalModalOpen] = useState(false);
    const [selectedProfessional, setSelectedProfessional] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/professionals");
                dispatch({ type: "LOAD_PROFESSIONALS", payload: data });
                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        })();
    }, []);

    const handleOpenProfessionalModal = () => {
        setSelectedProfessional(null);
        setProfessionalModalOpen(true);
    };

    const handleCloseProfessionalModal = () => {
        setProfessionalModalOpen(false);
        setSelectedProfessional(null);
        loadProfessionals();
    };

    const loadProfessionals = async () => {
        try {
            const { data } = await api.get("/professionals");
            dispatch({ type: "RESET" });
            dispatch({ type: "LOAD_PROFESSIONALS", payload: data });
        } catch (err) {
            toastError(err);
        }
    };

    const handleEditProfessional = (professional) => {
        setSelectedProfessional(professional);
        setProfessionalModalOpen(true);
    };

    const handleDeleteProfessional = async (professionalId) => {
        try {
            await api.delete(`/professionals/${professionalId}`);
            toast.success(i18n.t("professionalModal.toasts.deleted"));
            dispatch({ type: "DELETE_PROFESSIONAL", payload: professionalId });
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={selectedProfessional && `${i18n.t("professionalModal.confirmationModal.deleteTitle")} ${selectedProfessional.name}?`}
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => handleDeleteProfessional(selectedProfessional.id)}
            >
                {i18n.t("professionalModal.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <ProfessionalModal
                open={professionalModalOpen}
                onClose={handleCloseProfessionalModal}
                professionalId={selectedProfessional?.id}
                key={selectedProfessional?.id}
            />
            <Paper className={classes.mainPaper} variant="outlined">
                <MainHeader>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <PageHelpGuide
                            helpData={PROFESSIONALS_HELP_DATA}
                            tooltipText="Guía y Ayuda de Profesionales (Haz clic para ver)"
                        />
                        <Title>{i18n.t("professionalModal.title.list")} ({professionals.length})</Title>
                    </div>
                    <MainHeaderButtonsWrapper>
                        <Button
                            className={classes.applyButton}
                            onClick={handleOpenProfessionalModal}
                        >
                            {i18n.t("common.add")}
                        </Button>
                    </MainHeaderButtonsWrapper>
                </MainHeader>
                <TableContainer className={classes.scrollArea}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("professionalModal.table.name")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("professionalModal.table.email")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("professionalModal.table.services")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("professionalModal.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {professionals.map((professional) => (
                                <TableRow key={professional.id}>
                                    <TableCell align="center">{professional.name}</TableCell>
                                    <TableCell align="center">{professional.email}</TableCell>
                                    <TableCell align="center">
                                        {professional.services && professional.services.map(s => s.name).join(", ")}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditProfessional(professional)}
                                        >
                                            <Edit style={{ color: "#00a8ff" }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedProfessional(professional);
                                                setConfirmModalOpen(true);
                                            }}
                                        >
                                            <DeleteOutline style={{ color: "#e84118" }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={4} />}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Professionals;
